import { useMemo } from "react";
import { Button, Modal, InputDate, InputPercentage, Autocomplete, Icon } from "@artprov/design-system";
import { useModalFilter } from "../hooks/useModalFilter.js";

export default function ModalFilter() {
    const {
        // Estados
        isFilterModalOpen,
        setIsFilterModalOpen,
        resetKey,
        jurisdicciones,
        jurisdiccionesLoading,

        // Estados del formulario
        fechaAltaDesdeInput,
        fechaAltaHastaInput,
        fechaDebitoDesdeInput,
        fechaDebitoHastaInput,
        coincidenciaDesdeInput,
        coincidenciaHastaInput,
        idJurisdiccion,
        jurisdiccionSearch,
        errorFechaAltaDesde,
        errorFechaAltaHasta,
        errorFechaDebitoDesde,
        errorFechaDebitoHasta,
        errorCoincidenciaDesde,
        errorCoincidenciaHasta,
        errorJurisdiccion,

        // Handlers
        handleAccept,
        handleClear,
        handleFechaAltaDesdeChange,
        handleFechaAltaHastaChange,
        handleFechaDebitoDesdeChange,
        handleFechaDebitoHastaChange,
        handleCoincidenciaDesdeChange,
        handleCoincidenciaHastaChange,
        handleJurisdiccionChange,
        handleJurisdiccionBlur,
        setJurisdiccionSearch,
        setIdJurisdiccion,
        setErrorJurisdiccion,
        restaurarFiltrosAplicados,

        // Ref de montaje para Autocomplete
        isMounting,
    } = useModalFilter();

    // Opciones para Autocomplete de Jurisdicción - Memoizadas para evitar re-renders innecesarios
    const opcionesJurisdiccion = useMemo(() => {
        if (jurisdiccionesLoading) {
            return [];
        }

        if (jurisdicciones.length === 0) {
            return [];
        }

        return jurisdicciones.map((jurisdiccion) => ({
            id: String(jurisdiccion.value),
            descripcion: jurisdiccion.label,
        }));
    }, [jurisdicciones, jurisdiccionesLoading]);

    // Validaciones
    const hasErrors = errorFechaAltaDesde || errorFechaAltaHasta || errorFechaDebitoDesde || errorFechaDebitoHasta || errorCoincidenciaDesde || errorCoincidenciaHasta || errorJurisdiccion;
    const isAcceptDisabled = hasErrors;
    const isClearDisabled = !fechaAltaDesdeInput && !fechaAltaHastaInput && !fechaDebitoDesdeInput && !fechaDebitoHastaInput && !coincidenciaDesdeInput && !coincidenciaHastaInput && !idJurisdiccion && !jurisdiccionSearch;

    const handleCloseModal = () => {
        restaurarFiltrosAplicados();
        setIsFilterModalOpen(false);
    };

    return (
        <div>
            <Button
                variant="outline"
                size="sm"
                startIcon={<Icon icon="filter_list" size="medium" />}
                onClick={() => setIsFilterModalOpen(true)}
            >
                Filtros
            </Button>

            {isFilterModalOpen && (
                <Modal.Container
                    open={isFilterModalOpen}
                    setOpen={setIsFilterModalOpen}
                    closeOnClickOutside={false}
                    onClose={handleCloseModal}
                >
                    <Modal.Content height="auto" width="456px" className="py-5 px-5">
                        <Modal.Header>
                            <Modal.Title>Filtros</Modal.Title>
                            <Modal.Close onClose={handleCloseModal} />
                        </Modal.Header>

                        <div className="flex flex-col w-full">
                            {/* Primera fila: Fechas de alta */}
                            <div className="flex gap-4">
                                <InputDate
                                    key={`fecha-alta-desde-${resetKey}`}
                                    label="Fecha de alta desde"
                                    value={fechaAltaDesdeInput}
                                    onChange={handleFechaAltaDesdeChange}
                                    hints={[
                                        ...(errorFechaAltaDesde && errorFechaAltaDesde !== "Ingresá una fecha válida"
                                            ? [{ variant: 'error', text: errorFechaAltaDesde }]
                                            : [])
                                    ]}
                                    calendarRenderMode="portal"
                                    className="flex-1"
                                />
                                <InputDate
                                    key={`fecha-alta-hasta-${resetKey}`}
                                    label="Fecha de alta hasta"
                                    value={fechaAltaHastaInput}
                                    onChange={handleFechaAltaHastaChange}
                                    hints={[
                                        ...(errorFechaAltaHasta && errorFechaAltaHasta !== "Ingresá una fecha válida"
                                            ? [{ variant: 'error', text: errorFechaAltaHasta }]
                                            : [])
                                    ]}
                                    calendarRenderMode="portal"
                                    className="flex-1"
                                />
                            </div>

                            {/* Segunda fila: Fechas débito */}
                            <div className="flex gap-4">
                                <InputDate
                                    key={`fecha-desde-${resetKey}`}
                                    label="Fecha del débito desde"
                                    value={fechaDebitoDesdeInput}
                                    onChange={handleFechaDebitoDesdeChange}
                                    hints={[
                                        ...(errorFechaDebitoDesde && errorFechaDebitoDesde !== "Ingresá una fecha válida"
                                            ? [{ variant: 'error', text: errorFechaDebitoDesde }]
                                            : [])
                                    ]}
                                    calendarRenderMode="portal"
                                    className="flex-1"
                                />
                                <InputDate
                                    key={`fecha-hasta-${resetKey}`}
                                    label="Fecha del débito hasta"
                                    value={fechaDebitoHastaInput}
                                    onChange={handleFechaDebitoHastaChange}
                                    hints={[
                                        ...(errorFechaDebitoHasta && errorFechaDebitoHasta !== "Ingresá una fecha válida"
                                            ? [{ variant: 'error', text: errorFechaDebitoHasta }]
                                            : [])
                                    ]}
                                    calendarRenderMode="portal"
                                    className="flex-1"
                                />
                            </div>

                            {/* Segunda fila: Porcentajes de coincidencia */}
                            <div className="flex gap-4">
                                <InputPercentage
                                    key={`coincidencia-desde-${resetKey}`}
                                    label="Coincidencia desde"
                                    placeholder="0"
                                    value={coincidenciaDesdeInput}
                                    onChange={handleCoincidenciaDesdeChange}
                                    status={errorCoincidenciaDesde ? "error" : "default"}
                                    hints={[
                                        ...(errorCoincidenciaDesde ? [{ variant: 'error', text: errorCoincidenciaDesde }] : [])
                                    ]}
                                    decimal={false}
                                    className="flex-1"
                                />
                                <InputPercentage
                                    key={`coincidencia-hasta-${resetKey}`}
                                    label="Coincidencia hasta"
                                    placeholder="100"
                                    value={coincidenciaHastaInput}
                                    onChange={handleCoincidenciaHastaChange}
                                    status={errorCoincidenciaHasta ? "error" : "default"}
                                    hints={[
                                        ...(errorCoincidenciaHasta ? [{ variant: 'error', text: errorCoincidenciaHasta }] : [])
                                    ]}
                                    decimal={false}
                                    className="flex-1"
                                />
                            </div>

                            {/* Tercera fila: Jurisdicción */}
                            <Autocomplete
                                key={`jurisdiccion-${resetKey}`}
                                label="Jurisdicción"
                                placeholder="Seleccionar"
                                opciones={opcionesJurisdiccion}
                                disabled={jurisdiccionesLoading}
                                cargando={jurisdiccionesLoading}
                                searchValue={jurisdiccionSearch}
                                setSearchValue={(val) => {
                                    setJurisdiccionSearch(val);
                                    // Workaround: ignorar invalidación durante el montaje
                                    if (isMounting.current) return;
                                    // Solo invalidar si el texto no coincide con la opción actualmente seleccionada
                                    if (opcionesJurisdiccion.length > 0) {
                                        const currentOption = opcionesJurisdiccion.find((o) => Number(o.id) === idJurisdiccion);
                                        if (!currentOption || currentOption.descripcion !== val) {
                                            setIdJurisdiccion(null);
                                        }
                                    }
                                }}
                                setSelected={(option) => {
                                    if (option) {
                                        handleJurisdiccionChange({
                                            value: Number(option.id),
                                            label: option.descripcion
                                        });
                                    }
                                }}
                                onClear={() => {
                                    // Workaround: ignorar clear durante el montaje
                                    if (isMounting.current) return;
                                    setIdJurisdiccion(null);
                                    setJurisdiccionSearch("");
                                    setErrorJurisdiccion("");
                                }}
                                onBlur={handleJurisdiccionBlur}
                                hints={errorJurisdiccion ? [{ variant: 'error', text: errorJurisdiccion }] : []}
                                classNameContainer="w-full"
                            />
                        </div>

                        <Modal.Footer className="flex justify-end">
                            <div className="flex gap-2">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleClear}
                                    isDisabled={isClearDisabled}
                                >
                                    Borrar filtros
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleAccept}
                                    isDisabled={isAcceptDisabled}
                                >
                                    Ver resultados
                                </Button>
                            </div>
                        </Modal.Footer>
                    </Modal.Content>
                </Modal.Container>
            )}
        </div>
    );
}
